/**
 * Sync Entity Types
 * 
 * Defines all entity types that need to be synced from server to local encrypted storage.
 * Matches Flutter's SyncEntityType enum from lib/domain/enums/sync_entity_type.dart
 */
export enum SyncEntityType {
  DEVICE_ID = 'deviceId',
  LIKES = 'likes',
  COMMENT_LIKES = 'commentLikes',
  COMMUNITY_MESSAGES_LIKES = 'communityMessagesLikes',
  SAVED_CONTENT = 'savedContent',
  SAVED_USERS = 'savedUsers',
  BLOCKED_USERS = 'blockedUsers',
  MUTED_USERS = 'mutedUsers',
  USERS_BLOCKED_ME = 'usersBlockedMe',
  BLOCKED_CONVERSATIONS = 'blockedConversations',
  WALLET = 'wallet',
  EXCLUSIVE_CONTENT_SUBSCRIPTIONS = 'exclusiveContentSubscriptions',
  USER_SETTINGS = 'userSettings',
  USER_PREMIUM_PACKAGES = 'userPremiumPackages',
  PREMIUM_PACKAGES = 'premiumPackages',
  LOCAL_E2EE = 'localE2EE',
  GIFTS = 'gifts',
  RECHARGE_WALLET_PRODUCTS = 'rechargeWalletProducts',
  UPLOAD_PROFILE_IMAGE = 'uploadProfileImage',
  ROADMAP_LIKES = 'roadmapLikes',
  ROADMAP_COMMENT_LIKES = 'roadmapCommentLikes',
  DONATION_PRODUCTS = 'donationProducts',
  UNLOCKED_CONTENT = 'unlockedContent',
}

/**
 * The subset of `SyncEntityType` whose sync path needs no `UserEntity`.
 *
 * Everything these paths require comes from the server (or from the signed-in
 * Firebase user that `SyncService` reads itself), so they can be re-synced in
 * isolation without a user entity in hand — mirroring Flutter, where the
 * equivalent repository methods take no `User` argument
 * (`syncGifts()` / `syncRechargeWalletProducts()` / `syncPremiumPackages()` /
 * `syncDonationProducts()` / `_syncUserSettings()`, see
 * lib/data/repository/sync_repository.dart:99-109).
 *
 * This is exactly the set the Firebase Remote Config real-time listener
 * invalidates through `SyncSecureStorageUseCase.resyncEntity`, which is typed
 * against this union so that passing a user-dependent type (`BLOCKED_USERS`,
 * `MUTED_USERS`, `USERS_BLOCKED_ME`, `BLOCKED_CONVERSATIONS`,
 * `UPLOAD_PROFILE_IMAGE`) is a compile error rather than a runtime surprise.
 */
export type UserIndependentSyncEntityType =
  | SyncEntityType.GIFTS
  | SyncEntityType.RECHARGE_WALLET_PRODUCTS
  | SyncEntityType.PREMIUM_PACKAGES
  | SyncEntityType.DONATION_PRODUCTS
  | SyncEntityType.USER_SETTINGS;

/**
 * Configuration for sync entity types
 */
export interface SyncEntityConfig {
  type: SyncEntityType;
  /** Whether to wait for this sync to complete before proceeding */
  wait: boolean;
}

/**
 * Get sync configuration for entity type
 */
export function getSyncConfig(type: SyncEntityType): SyncEntityConfig {
  // Only wallet sync should wait (critical for app functionality)
  const wait = type === SyncEntityType.WALLET;
  
  return { type, wait };
}

/**
 * Get all sync entity types in execution order
 */
export function getAllSyncEntityTypes(): SyncEntityType[] {
  return Object.values(SyncEntityType);
}
